const TIMEOUT_MS = 10_000;
const MAX_DIMENSION = 720;

export async function generateVideoPoster(videoFile: File): Promise<{ blob: Blob; width: number; height: number } | null> {
	const objectUrl = URL.createObjectURL(videoFile);
	const video = document.createElement("video");
	video.muted = true;
	video.playsInline = true;
	video.preload = "metadata";
	video.src = objectUrl;

	try {
		return await withTimeout(capturePoster(video), TIMEOUT_MS);
	} catch {
		return null;
	} finally {
		URL.revokeObjectURL(objectUrl);
	}
}

async function capturePoster(video: HTMLVideoElement): Promise<{ blob: Blob; width: number; height: number } | null> {
	await waitForEvent(video, "loadedmetadata");
	video.currentTime = 0.1;
	await waitForEvent(video, "seeked");

	const intrinsicW = video.videoWidth;
	const intrinsicH = video.videoHeight;
	if (!intrinsicW || !intrinsicH) return null;

	const scale = Math.min(1, MAX_DIMENSION / Math.max(intrinsicW, intrinsicH));
	const width = Math.round(intrinsicW * scale);
	const height = Math.round(intrinsicH * scale);

	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;
	const ctx = canvas.getContext("2d");
	if (!ctx) return null;
	ctx.drawImage(video, 0, 0, width, height);

	const blob = await new Promise<Blob | null>((resolve) => {
		canvas.toBlob((b) => resolve(b), "image/webp", 0.7);
	});
	if (!blob) return null;
	return { blob, width, height };
}

function waitForEvent(el: HTMLVideoElement, name: "loadedmetadata" | "seeked"): Promise<void> {
	return new Promise((resolve, reject) => {
		const onLoad = () => {
			cleanup();
			resolve();
		};
		const onError = () => {
			cleanup();
			reject(new Error(`video ${name} failed`));
		};
		const cleanup = () => {
			el.removeEventListener(name, onLoad);
			el.removeEventListener("error", onError);
		};
		el.addEventListener(name, onLoad, { once: true });
		el.addEventListener("error", onError, { once: true });
	});
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
	return new Promise((resolve, reject) => {
		const id = setTimeout(() => reject(new Error("timeout")), ms);
		promise.then(
			(value) => {
				clearTimeout(id);
				resolve(value);
			},
			(err) => {
				clearTimeout(id);
				reject(err);
			},
		);
	});
}
